/* eslint-disable arrow-body-style */

import { Table } from 'tea-component';
import { cloneDeep } from 'lodash-es';
import { charsComparator, generateDateComparator } from '@gp/utils/common';
import { METRICS_DEFAULT_ACTIVE_ID } from '../constants';

import type { SortBy, SortableColumn } from 'tea-component/lib/table/addons';
import type { IMetricsCategory, IMetricsBaseMetric, ICategoryMetricsData } from '@gp/types/metrics';
import type { IMetricsTab } from '../types';

const { sortable } = Table.addons;

export const getMatchedActiveId = (tabs?: IMetricsTab[], activeId?: IMetricsCategory): IMetricsCategory => {
  if (!Array.isArray(tabs) || !tabs.length) return METRICS_DEFAULT_ACTIVE_ID;
  const tab = tabs.find((tab) => tab?.id === activeId);
  return tab?.id || tabs[0]?.id || METRICS_DEFAULT_ACTIVE_ID;
};

export const getMetricsDataSafety = (metricsData?: ICategoryMetricsData | null): ICategoryMetricsData => {
  const {
    project_metrics: projectMetrics,
    studio_financial_metrics: studioFinancialMetrics,
    common_system_terminology: commonSystemTerminology,
  } = metricsData || {};
  return {
    project_metrics: projectMetrics || [],
    studio_financial_metrics: studioFinancialMetrics || [],
    common_system_terminology: commonSystemTerminology || [],
  };
};

export const resetPageScrollTop = () => {
  document.querySelector('.dbg-metrics-page')?.scrollTo(0, 0);
};

export const getMetricsTableRecordKey = (record?: IMetricsBaseMetric | null): string => {
  const { id } = record || {};
  return id || '';
};

export const generateCharsComparator = <T extends IMetricsBaseMetric>(key: keyof T) => {
  return (a: T, b: T) => {
    return charsComparator(a?.[key] as string, b?.[key] as string);
  };
};

export const buildSortableColumns = <T extends IMetricsBaseMetric>(keys: (keyof T)[]): SortableColumn<T>[] => {
  if (!Array.isArray(keys) || !keys.length) return [];

  // 按日期排序的字段名
  const dateKeys = ['earliest_data'] as (keyof T)[];

  return keys.map<SortableColumn<T>>((key) => {
    const comparator = dateKeys.includes(key) ? generateDateComparator(key as string) : generateCharsComparator(key);

    return {
      key: key as string,
      prefer: 'asc',
      sorter: comparator,
    };
  });
};

export const getSortedRecords = <T extends IMetricsBaseMetric>(list?: T[], sorts?: SortBy<T>[]): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  if (!Array.isArray(sorts) || !sorts.length) return list;

  const { by } = sorts[0];

  return cloneDeep(list).sort((a, b) => {
    // a无数据时，a固定排最后
    if (!a?.[by as keyof T]) return 1;
    // b无数据时，b固定排最后
    if (!b?.[by as keyof T]) return -1;
    return sortable.comparer(sorts)(a, b);
  });
};
